import React, { useCallback, useEffect, useState } from "react";
import { Box, MenuItem, TextField, Typography, makeStyles } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Badge, Button, AsyncSection } from "../ds";
import tokens from "../designTokens";
import { agentDreams, dreamPromotion, dreamPromotionImpact, promoteDream, revertDream, revertDreamFacts, unpromoteDream } from "./api";
import { fmtRate, fmtTime } from "./shared";

const useStyles = makeStyles({
  head: { fontFamily: tokens.fontBody, fontWeight: 600, fontSize: 12.5, color: tokens.textPrimary, margin: "14px 0 6px" },
  row: { fontFamily: tokens.fontBody, fontSize: 12.5, color: tokens.textPrimary, padding: "6px 0", borderBottom: `1px solid ${tokens.divider}` },
  sub: { fontFamily: tokens.fontBody, fontSize: 11.5, color: tokens.textHint },
  col: { flex: 1, minWidth: 0, background: "#FAFBFC", border: `1px solid ${tokens.border}`, borderRadius: 6, padding: 10 },
  line: { fontFamily: tokens.fontBody, fontSize: 12, lineHeight: 1.5, color: tokens.textPrimary, marginBottom: 4, wordBreak: "break-word" },
});

const FACT_RE = /^(.*?)(\s*\([^()]*#[^()]*\))\s*$/;
const FactLine = ({ text, className, mutedClass }) => {
  const m = FACT_RE.exec(text);
  return <div className={className}>{m ? <>{m[1]}<span className={mutedClass}>{m[2]}</span></> : text}</div>;
};

const asLines = (v) => (Array.isArray(v) ? v : v ? String(v).split("\n") : []);

const REVERT_409 = "The Learned memory has changed since this dream, so it was not reverted. Review MEMORY.md manually.";
const REVERT_404 = "This dream has no memory change to revert.";
const FACTS_409 = "FACTS.md has changed since this dream, so it was not reverted. Review FACTS.md manually.";
const FACTS_404 = "This dream has no FACTS.md change to revert.";

export default function DreamsPanel({ projectId }) {
  const classes = useStyles();
  const [date, setDate] = useState(null);
  const [state, setState] = useState({ loading: true, notFound: false, error: null, data: null });
  const [reverting, setReverting] = useState(false);
  const [revertMsg, setRevertMsg] = useState(null);
  const [revertingFacts, setRevertingFacts] = useState(false);
  const [factsMsg, setFactsMsg] = useState(null);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    return agentDreams(projectId, { limit: 30 }).then((r) => setState({ ...r, loading: false }));
  }, [projectId]);

  useEffect(() => {
    setDate(null);
    setRevertMsg(null);
    setFactsMsg(null);
    load();
  }, [load]);

  const dreams = state.data?.dreams || [];
  const dream = dreams.find((d) => d.date === date) || dreams[0];
  const gaps = dream?.gaps;
  const gapItems = gaps?.items || [];
  const diff = dream?.memory_diff;
  const stats = dream?.stats;
  const picks = dream?.picks || [];
  const factsDiff = dream?.facts_diff;
  const fs = dream?.facts_stats;
  const showFacts = !!(fs?.overflow || factsDiff);

  const onRevertFacts = async () => {
    if (!window.confirm(`Revert FACTS.md to its state before the ${dream.date} dream?`)) return;
    setRevertingFacts(true);
    const r = await revertDreamFacts(projectId, dream.date, dream.agent_id);
    setRevertingFacts(false);
    if (r.status === 409) setFactsMsg({ severity: "warning", text: FACTS_409 });
    else if (r.notFound) setFactsMsg({ severity: "warning", text: FACTS_404 });
    else if (r.error) setFactsMsg({ severity: "error", text: r.error });
    else {
      setFactsMsg({ severity: "success", text: "Reverted." });
      load();
    }
  };

  const onRevert = async () => {
    if (!window.confirm(`Revert MEMORY.md "Learned" to its state before the ${dream.date} dream?`)) return;
    setReverting(true);
    const r = await revertDream(projectId, dream.date, dream.agent_id);
    setReverting(false);
    if (r.status === 409) setRevertMsg({ severity: "warning", text: REVERT_409 });
    else if (r.notFound) setRevertMsg({ severity: "warning", text: REVERT_404 });
    else if (r.error) setRevertMsg({ severity: "error", text: r.error });
    else {
      setRevertMsg({ severity: "success", text: "Reverted." });
      load();
    }
  };

  return (
    <AsyncSection loading={state.loading} notFound={false} error={state.notFound ? null : state.error} empty={state.notFound || !dreams.length} emptyTitle="No dream yet" emptyDescription="No dream recorded for this agent yet. Dreams run nightly.">
      {dream && (
        <div>
          {revertMsg && <Alert severity={revertMsg.severity} style={{ marginBottom: 10 }}>{revertMsg.text}</Alert>}
          <Box display="flex" alignItems="center" gridGap={8} flexWrap="wrap">
            <TextField select size="small" variant="outlined" label="Dream date" value={dream.date} onChange={(e) => { setDate(e.target.value); setRevertMsg(null); setFactsMsg(null); }} style={{ minWidth: 160 }}>
              {dreams.map((d) => <MenuItem key={d.date} value={d.date}>{d.date}</MenuItem>)}
            </TextField>
            {dream.archived === false && <Badge tone="warning" subtle>archive failed</Badge>}
            {dream.reverted_at && <Badge tone="neutral" subtle>reverted</Badge>}
          </Box>

          <Typography component="div" className={classes.head}>Gaps</Typography>
          {gaps?.error ? (
            <Alert severity="warning">Gap clustering failed ({gaps.error}); {gaps.failed_episodes_24h ?? 0} failed episodes in the last 24h.</Alert>
          ) : (
            <>
              <Typography component="div" className={classes.sub}>
                {gaps?.ledger_questions ?? 0} unanswered questions · {gaps?.failed_episodes_24h ?? 0} failed episodes (24h)
              </Typography>
              {gapItems.length ? gapItems.map((g) => (
                <div key={g.label} className={classes.row}>
                  <Box display="flex" gridGap={8} alignItems="center">
                    <span>{g.label}</span>
                    <Badge tone="neutral" subtle>{g.count}</Badge>
                    {g.kind === "action" && <Badge tone="warning" subtle>action{g.action ? ` · ${g.action}` : ""}</Badge>}
                  </Box>
                  {(g.samples || []).map((sample, i) => <div key={i} className={classes.sub}>{sample}</div>)}
                </div>
              )) : <Typography className={classes.sub}>No gaps found.</Typography>}
            </>
          )}

          <Box display="flex" alignItems="center" style={{ margin: "14px 0 6px" }}>
            <Typography component="div" className={classes.head} style={{ margin: 0 }}>Memory "Learned" diff</Typography>
            {diff && (
              <span style={{ marginLeft: "auto" }}>
                <Button size="sm" variant="danger" disabled={reverting || !!dream.reverted_at} onClick={onRevert}>Revert</Button>
              </span>
            )}
          </Box>
          {dream.memory_error && <Alert severity="warning" style={{ marginBottom: 8 }}>Memory edit failed ({dream.memory_error}); showing the previous diff if any.</Alert>}
          {diff ? (
            <Box display="flex" gridGap={10}>
              {["before", "after"].map((k) => (
                <div key={k} className={classes.col}>
                  <Typography component="div" className={classes.sub} style={{ marginBottom: 6 }}>{k}</Typography>
                  {asLines(diff[k]).length ? asLines(diff[k]).map((l, i) => <div key={i} className={classes.line}>{l}</div>) : <span className={classes.sub}>empty</span>}
                </div>
              ))}
            </Box>
          ) : <Typography className={classes.sub}>No memory change.</Typography>}

          {showFacts && (
            <>
              <Box display="flex" alignItems="center" gridGap={8} style={{ margin: "14px 0 6px" }}>
                <Typography component="div" className={classes.head} style={{ margin: 0 }}>Knowledge notebook (FACTS.md)</Typography>
                {fs?.retired && <Badge tone="neutral" subtle>retired</Badge>}
                {fs?.overflow && <Badge tone="warning" subtle>overflow</Badge>}
                {dream.facts_reverted_at && <Badge tone="neutral" subtle>reverted</Badge>}
                {factsDiff && (
                  <span style={{ marginLeft: "auto" }}>
                    <Button size="sm" variant="danger" disabled={revertingFacts || !!dream.facts_reverted_at} onClick={onRevertFacts}>Revert facts</Button>
                  </span>
                )}
              </Box>
              {factsMsg && <Alert severity={factsMsg.severity} style={{ marginBottom: 8 }}>{factsMsg.text}</Alert>}
              {dream.facts_error && (
                <Alert severity="warning" style={{ marginBottom: 8 }}>
                  {dream.facts_error === "facts_conflict" ? "FACTS.md changed while the dream ran; the edit was not applied." : "FACTS.md edit failed."} ({dream.facts_error})
                </Alert>
              )}
              {fs && (
                <Typography component="div" className={classes.sub} style={{ marginBottom: 6 }}>
                  {fs.lines ?? 0} lines · {fs.tokens ?? 0} tokens · +{fs.added ?? 0} added, {fs.merged ?? 0} merged, {fs.deleted ?? 0} deleted, {fs.rejected ?? 0} rejected, {fs.invalidated ?? 0} invalidated · {fs.questions ?? 0} questions, {fs.pages_read ?? 0} pages read · ${Number(fs.cost_usd ?? 0).toFixed(4)}
                </Typography>
              )}
              {factsDiff ? (
                <Box display="flex" gridGap={10}>
                  {["before", "after"].map((k) => (
                    <div key={k} className={classes.col}>
                      <Typography component="div" className={classes.sub} style={{ marginBottom: 6 }}>{k}</Typography>
                      {asLines(factsDiff[k]).length ? asLines(factsDiff[k]).map((l, i) => <FactLine key={i} text={l} className={classes.line} mutedClass={classes.sub} />) : <span className={classes.sub}>empty</span>}
                    </div>
                  ))}
                </Box>
              ) : <Typography className={classes.sub}>No FACTS.md change.</Typography>}
            </>
          )}

          <JudgeSection judge={dream.judge} classes={classes} />

          <PromotionSection projectId={projectId} agentId={dream.agent_id} classes={classes} />

          {stats && (
            <>
              <Typography component="div" className={classes.head}>Day stats</Typography>
              <Typography component="div" className={classes.sub} style={{ marginBottom: 6 }}>{stats.judged ?? 0} judged of {stats.episodes ?? 0} episodes</Typography>
              <Box display="flex" flexWrap="wrap" gridGap={6}>
                {Object.entries(stats.goal_rate_by_motion || {}).map(([m, r]) => <Badge key={m} tone="neutral" subtle>{m} {fmtRate(r)}</Badge>)}
                {stats.critical_bad_rate != null && <Badge tone={stats.critical_bad_rate > 0 ? "error" : "success"} subtle>critical-bad {fmtRate(stats.critical_bad_rate)}</Badge>}
                {Object.entries(stats.handoff || {}).map(([k, v]) => <Badge key={k} tone="neutral" subtle>handoff {k} {v}</Badge>)}
                {Object.entries(stats.issue_counts || {}).map(([k, v]) => <Badge key={k} tone="warning" subtle>{k} {v}</Badge>)}
              </Box>
            </>
          )}

          <ToolErrors toolErrors={dream.tool_errors} classes={classes} />

          {picks.length > 0 && (
            <>
              <Typography component="div" className={classes.head}>Review picks</Typography>
              {picks.map((p) => (
                <div key={p.verdict_id} className={classes.row}>
                  {p.verdict_id}{" "}
                  <span className={classes.sub}>
                    {(p.reasons || []).join(", ")}
                    {(p.turn_refs || []).length ? ` · turns ${p.turn_refs.join(", ")}` : ""}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </AsyncSection>
  );
}

function JudgeSection({ judge, classes }) {
  if (!judge) return null;
  const sm = judge.summary;
  const dropped = [...(judge.facts || []).filter((f) => !f.keep).map((f) => `FACTS: ${f.line}`), ...(judge.learned || []).filter((l) => !l.keep).map((l) => `Learned: ${l.line}`)];
  return (
    <>
      <Typography component="div" className={classes.head}>Dream judge</Typography>
      {judge.error ? (
        <Alert severity="warning">Not judged ({judge.error}); nothing was dropped and this night does not count toward readiness.</Alert>
      ) : sm ? (
        <>
          <Box display="flex" flexWrap="wrap" gridGap={6} style={{ marginBottom: 6 }}>
            <Badge tone={sm.supported == null || sm.supported >= 0.85 ? "success" : "warning"} subtle>supported {fmtRate(sm.supported)} of {sm.facts}</Badge>
            <Badge tone={sm.misleading ? "error" : "success"} subtle>misleading {fmtRate(sm.misleading)}</Badge>
            <Badge tone="neutral" subtle>dropped {sm.facts_dropped}</Badge>
            <Badge tone={sm.learned_safe == null || sm.learned_safe >= 0.95 ? "success" : "error"} subtle>learned safe {fmtRate(sm.learned_safe)} of {sm.learned}</Badge>
            <Badge tone="neutral" subtle>gaps knowledge {fmtRate(sm.gaps_knowledge)}</Badge>
            <Badge tone="neutral" subtle>labels ok {fmtRate(sm.gap_labels_ok)}</Badge>
            <Badge tone="neutral" subtle>${Number(judge.cost_usd || 0).toFixed(4)}</Badge>
          </Box>
          {dropped.map((l, i) => <div key={i} className={classes.sub}>dropped · {l}</div>)}
        </>
      ) : null}
    </>
  );
}

function ToolErrors({ toolErrors, classes }) {
  if (!toolErrors) return null;
  const tools = toolErrors.tools || [];
  return (
    <>
      <Typography component="div" className={classes.head}>Tool calls</Typography>
      {toolErrors.error ? (
        <Alert severity="warning">Tool events could not be read ({toolErrors.error}).</Alert>
      ) : tools.length ? (
        <>
          <Typography component="div" className={classes.sub} style={{ marginBottom: 6 }}>{toolErrors.calls ?? 0} calls · {toolErrors.failed ?? 0} failed</Typography>
          {tools.map((t) => (
            <div key={t.tool} className={classes.row}>
              <Box display="flex" gridGap={8} alignItems="center" flexWrap="wrap">
                <span>{t.tool}</span>
                <Badge tone="neutral" subtle>{t.calls} calls</Badge>
                {t.failed > 0 && <Badge tone={t.fail_rate > 0.2 ? "error" : "warning"} subtle>{t.failed} failed · {fmtRate(t.fail_rate)}</Badge>}
                <span className={classes.sub}>{Object.entries(t.types || {}).map(([k, v]) => `${k} ${v}`).join(" · ")}</span>
              </Box>
            </div>
          ))}
        </>
      ) : <Typography className={classes.sub}>No tool calls in this night's judged episodes.</Typography>}
    </>
  );
}

const VERDICT_TONE = { improved: "success", flat: "neutral", too_early: "neutral", regressed: "error" };

function PromotionSection({ projectId, agentId, classes }) {
  const [cand, setCand] = useState(null);
  const [impact, setImpact] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    if (!agentId) return;
    dreamPromotion(projectId, agentId).then((r) => setCand(r.data));
    dreamPromotionImpact(projectId, agentId).then((r) => setImpact(r.data?.impact || null));
  }, [projectId, agentId]);

  useEffect(() => {
    setMsg(null);
    load();
  }, [load]);

  const lines = cand?.lines || [];
  const onPromote = async () => {
    if (!window.confirm(`Add ${lines.length} line(s) to this agent's live Learned memory? Customers see the change on the next turn.`)) return;
    setBusy(true);
    const r = await promoteDream(projectId, agentId);
    setBusy(false);
    if (r.status === 409) setMsg({ severity: "warning", text: "Live memory changed during the write; try again." });
    else if (r.error || r.notFound) setMsg({ severity: "error", text: r.error || "Promotion is not available." });
    else {
      setMsg({ severity: "success", text: `Promoted ${(r.data?.promoted || []).length} line(s).` });
      load();
    }
  };
  const onUnpromote = async () => {
    if (!window.confirm(`Remove the lines promoted on ${impact.date} from live Learned memory? They will not be promoted again.`)) return;
    setBusy(true);
    const r = await unpromoteDream(projectId, impact.date, agentId);
    setBusy(false);
    if (r.status === 409) setMsg({ severity: "warning", text: "Live memory changed during the write; try again." });
    else if (r.error || r.notFound) setMsg({ severity: "error", text: r.error || "Nothing was promoted on that date." });
    else {
      setMsg({ severity: "success", text: `Removed ${(r.data?.removed || []).length} line(s).` });
      load();
    }
  };

  if (!cand && !impact) return null;
  const reason = { not_enough_nights: "fewer dreams than needed", nights_not_consecutive: "the last dreams are not on consecutive nights" }[cand?.reason];
  return (
    <>
      <Box display="flex" alignItems="center" style={{ margin: "14px 0 6px" }}>
        <Typography component="div" className={classes.head} style={{ margin: 0 }}>Promotion to live memory</Typography>
        {lines.length > 0 && (
          <span style={{ marginLeft: "auto" }}>
            <Button size="sm" disabled={busy} onClick={onPromote}>Promote {lines.length}</Button>
          </span>
        )}
      </Box>
      {msg && <Alert severity={msg.severity} style={{ marginBottom: 8 }}>{msg.text}</Alert>}
      {cand && (
        <Typography component="div" className={classes.sub} style={{ marginBottom: 4 }}>
          {lines.length
            ? `Proposed on each of the last ${cand.nights} nights (${(cand.dates || []).join(", ")}):`
            : `Nothing to promote${reason ? `: ${reason}` : ""}. A line qualifies when the dream proposes it ${cand.nights} nights in a row.`}
        </Typography>
      )}
      {lines.map((l, i) => <div key={i} className={classes.line}>{l}</div>)}
      {impact && (
        <div className={classes.col} style={{ marginTop: 8 }}>
          <Box display="flex" alignItems="center" gridGap={6} flexWrap="wrap">
            <span className={classes.line} style={{ margin: 0 }}>Last promotion {fmtTime(impact.promoted_at)}</span>
            <Badge tone={VERDICT_TONE[impact.verdict] || "neutral"} subtle>{impact.verdict}</Badge>
            {impact.unpromoted_at && <Badge tone="neutral" subtle>unpromoted</Badge>}
            {!impact.unpromoted_at && (
              <span style={{ marginLeft: "auto" }}>
                <Button size="sm" variant={impact.verdict === "regressed" ? "danger" : "outlined"} disabled={busy} onClick={onUnpromote}>Unpromote</Button>
              </span>
            )}
          </Box>
          <Typography component="div" className={classes.sub} style={{ marginTop: 4 }}>
            goal {fmtRate(impact.before?.goal_rate)} → {fmtRate(impact.after?.goal_rate)} · bad claims {fmtRate(impact.before?.bad_claim_rate)} → {fmtRate(impact.after?.bad_claim_rate)} · missed handoff {fmtRate(impact.before?.handoff_missed_rate)} → {fmtRate(impact.after?.handoff_missed_rate)} · episodes {impact.before?.episodes ?? 0} / {impact.after?.episodes ?? 0}
          </Typography>
          {(impact.lines || []).map((l, i) => <div key={i} className={classes.sub}>{l}</div>)}
        </div>
      )}
    </>
  );
}
